import {
  Bell,
  ChevronsLeftRight,
  Link,
  LockKeyhole,
  type LucideIcon,
  Palette,
  Sparkles,
  UserCheck,
} from "lucide-react";

import { routes } from "~/routes";

/** What must hold for a personal settings entry to be offered; every guard listed must pass. */
export type SettingsGuard = "passwordChangeableHere" | "aiChat";

export type SettingsGuardContext = {
  /** False when the sign-in provider owns the password (identity v2), so the app cannot change it. */
  passwordChangeableHere: boolean;
  aiChatEnabled: boolean;
};

export type SettingsEntryId =
  "profile" | "password" | "notifications" | "application" | "developer" | "ai-instructions" | "linked-accounts";

export type SettingsEntry = {
  id: SettingsEntryId;
  /** Navigation label; the settings header omits the page crumb and shows only "Settings". */
  label: string;
  /** Document title the route sets. */
  title: string;
  path: string;
  icon: LucideIcon;
  guards: SettingsGuard[];
  /** Nav hides the entry when guards fail, but the route still renders (e.g. IdP-managed password notice). */
  directRouteWhenNavHidden?: boolean;
};

export function settingsEntryAllowed(entry: SettingsEntry, context: SettingsGuardContext): boolean {
  return entry.guards.every((guard) => settingsGuardPasses(guard, context));
}

/** Where each retired `/settings?tab=` value lands, so old bookmarks and deep links still open their page. */
export const legacySettingsTabPaths = {
  "profile-settings": routes.SettingsProfile,
  "change-password": routes.SettingsPassword,
  "application-settings": routes.SettingsApplication,
  "developer-settings": routes.SettingsDeveloper,
  connections: routes.SettingsLinkedAccounts,
} as const satisfies Record<string, string>;

export function legacySettingsTabTarget(tab: string | null): string {
  if (tab && Object.hasOwn(legacySettingsTabPaths, tab)) {
    return legacySettingsTabPaths[tab as keyof typeof legacySettingsTabPaths];
  }
  return routes.SettingsProfile;
}

export const settingsEntries: SettingsEntry[] = [
  {
    id: "profile",
    label: "Profile",
    title: "Profile Settings",
    path: routes.SettingsProfile,
    icon: UserCheck,
    guards: [],
  },
  {
    id: "password",
    label: "Password",
    title: "Password",
    path: routes.SettingsPassword,
    icon: LockKeyhole,
    guards: ["passwordChangeableHere"],
    directRouteWhenNavHidden: true,
  },
  {
    id: "notifications",
    label: "Notifications",
    title: "Notification Settings",
    path: routes.SettingsNotifications,
    icon: Bell,
    guards: [],
  },
  {
    id: "application",
    label: "Application",
    title: "Application Settings",
    path: routes.SettingsApplication,
    icon: Palette,
    guards: [],
  },
  {
    id: "developer",
    label: "Developer",
    title: "Developer Settings",
    path: routes.SettingsDeveloper,
    icon: ChevronsLeftRight,
    guards: [],
  },
  {
    id: "ai-instructions",
    label: "AI Instructions",
    title: "AI Instructions",
    path: routes.SettingsAiInstructions,
    icon: Sparkles,
    guards: ["aiChat"],
  },
  {
    id: "linked-accounts",
    label: "Linked Accounts",
    title: "Linked Accounts",
    path: routes.SettingsLinkedAccounts,
    icon: Link,
    guards: [],
  },
];

function settingsGuardPasses(guard: SettingsGuard, context: SettingsGuardContext): boolean {
  switch (guard) {
    case "passwordChangeableHere":
      return context.passwordChangeableHere;
    case "aiChat":
      return context.aiChatEnabled;
  }
}
